/**
 * Normalizes the several error shapes the app can receive into one value the UI
 * can branch on.
 *
 * Three shapes reach us:
 *  - `PalantirApiError` thrown by OSDK calls (`fetchPage`, `applyAction`, ...)
 *  - the plain `{ error }` objects returned by `fetchPageWithErrors` / `fetchOneWithErrors`
 *  - `Error`s we throw ourselves around `fetch` against the backend
 */

export type ErrorKind =
  | "unauthenticated"
  | "permissionDenied"
  | "notFound"
  | "network"
  | "server"
  | "unknown";

export interface NormalizedError {
  kind: ErrorKind;
  statusCode?: number;
  errorName?: string;
  /** Foundry's per-occurrence id. Support needs this to trace a failure. */
  errorInstanceId?: string;
  message: string;
}

export interface ErrorPresentation {
  title: string;
  description: string;
  /** What the retry affordance should do. */
  action: "retry" | "reauthenticate" | "contactSupport";
  actionLabel: string;
}

const SUPPORT_EMAIL = "accounts@unrulycorp.com";

/**
 * Foundry does not always set a status code on the error body, so these names
 * are a fallback for classifying an error that arrived without one.
 */
const ERROR_NAME_KINDS: Record<string, ErrorKind> = {
  InvalidToken: "unauthenticated",
  TokenExpired: "unauthenticated",
  Unauthorized: "unauthenticated",
  MissingCredentials: "unauthenticated",
  PermissionDenied: "permissionDenied",
  Forbidden: "permissionDenied",
  ViewObjectPermissionDenied: "permissionDenied",
  ObjectTypeNotFound: "notFound",
  ObjectNotFound: "notFound",
  ResourceNotFound: "notFound",
};

function readString(source: Record<string, unknown>, key: string) {
  const value = source[key];
  return typeof value === "string" && value !== "" ? value : undefined;
}

function readStatusCode(source: Record<string, unknown>) {
  for (const key of ["statusCode", "status"]) {
    const value = source[key];
    if (typeof value === "number") {
      return value;
    }
  }
  const response = source.response;
  if (response !== null && typeof response === "object") {
    const status = (response as Record<string, unknown>).status;
    if (typeof status === "number") {
      return status;
    }
  }
  return undefined;
}

function kindFromStatusCode(statusCode: number): ErrorKind {
  if (statusCode === 401) return "unauthenticated";
  if (statusCode === 403) return "permissionDenied";
  if (statusCode === 404) return "notFound";
  if (statusCode >= 500) return "server";
  return "unknown";
}

/**
 * A failed `fetch` rejects with a TypeError carrying a browser-specific message,
 * which is the only signal available that the request never reached the server.
 */
function isNetworkFailure(error: unknown, message: string) {
  return (
    error instanceof TypeError &&
    /fetch|network|connection|load failed/i.test(message)
  );
}

export function normalizeError(error: unknown): NormalizedError {
  if (error === null || error === undefined) {
    return { kind: "unknown", message: "Unknown error" };
  }

  if (typeof error === "string") {
    return { kind: "unknown", message: error };
  }

  if (typeof error !== "object") {
    return { kind: "unknown", message: String(error) };
  }

  const source = error as Record<string, unknown>;
  const message =
    readString(source, "message") ??
    readString(source, "errorDescription") ??
    "Unknown error";
  const errorName =
    readString(source, "errorName") ?? readString(source, "errorType");
  const statusCode = readStatusCode(source);
  const errorInstanceId = readString(source, "errorInstanceId");

  let kind: ErrorKind = "unknown";
  if (statusCode !== undefined) {
    kind = kindFromStatusCode(statusCode);
  }
  if (kind === "unknown" && errorName && errorName in ERROR_NAME_KINDS) {
    kind = ERROR_NAME_KINDS[errorName];
  }
  if (kind === "unknown" && isNetworkFailure(error, message)) {
    kind = "network";
  }

  return { kind, statusCode, errorName, errorInstanceId, message };
}

/**
 * An expired session and a permissions problem both surface as "can't read your
 * user", but the user can only act on the first one, so they must read
 * differently.
 */
export function describeError(error: unknown): ErrorPresentation {
  const { kind } = normalizeError(error);

  switch (kind) {
    case "unauthenticated":
      return {
        title: "Your session has expired",
        description:
          "You have been signed out, or your session is no longer valid. Sign in again to continue.",
        action: "reauthenticate",
        actionLabel: "Sign in again",
      };
    case "permissionDenied":
      return {
        title: "You do not have access to this",
        description: `Your account is not permitted to view this content. If you think this is a mistake, contact Unruly support at ${SUPPORT_EMAIL}.`,
        action: "contactSupport",
        actionLabel: "Try again",
      };
    case "notFound":
      return {
        title: "We could not find this",
        description:
          "The content you are looking for does not exist, or has been removed.",
        action: "retry",
        actionLabel: "Try again",
      };
    case "network":
      return {
        title: "We could not reach Unruly",
        description:
          "Check your internet connection and try again. If you are on a VPN, it may be blocking the connection.",
        action: "retry",
        actionLabel: "Try again",
      };
    case "server":
      return {
        title: "Something went wrong on our end",
        description: `This is a problem with our service, not with your account. Please try again in a moment, and contact ${SUPPORT_EMAIL} if it keeps happening.`,
        action: "retry",
        actionLabel: "Try again",
      };
    default:
      return {
        title: "Something went wrong",
        description: `We could not load this content. Please try again, and contact Unruly support at ${SUPPORT_EMAIL} if the problem persists.`,
        action: "retry",
        actionLabel: "Try again",
      };
  }
}

/**
 * Retrying a 4xx just repeats the same rejection, and each attempt keeps the UI
 * in a loading state for longer before it can show the real problem.
 */
export function isRetryableError(error: unknown): boolean {
  const { statusCode } = normalizeError(error);
  if (statusCode === undefined) {
    return true;
  }
  if (statusCode === 408 || statusCode === 429) {
    return true;
  }
  return statusCode < 400 || statusCode >= 500;
}
